import { useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { adoptionApi } from '@/features/adoption/api';
import { sheltersApi } from '@/features/shelters/api';
import type { AdoptionFormSchema, AdoptionFormTemplate, FieldType, FormField, FormSection } from '@/features/adoption/types';
import { ArrowDown, ArrowUp, Copy, Loader2, Plus, Save, Trash2 } from 'lucide-react';

const FORM_CODE = 'default_adoption_form';
const GLOBAL_VALUE = '__global__';

const FIELD_TYPES: Array<{ value: FieldType; label: string }> = [
  { value: 'text', label: 'Texto corto' },
  { value: 'textarea', label: 'Texto largo' },
  { value: 'select', label: 'Lista desplegable' },
  { value: 'yesno', label: 'Sí / No' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Fecha' },
  { value: 'checkbox', label: 'Casilla' },
];

const PROFILE_COLUMNS = [
  'city',
  'address',
  'housing_type',
  'housing_ownership',
  'has_children',
  'children_ages',
  'has_elderly',
  'has_other_pets',
  'other_pets_desc',
  'vehicle_available',
  'notes',
];

function slugify(input: string) {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

function newField(label = 'Nueva pregunta'): FormField {
  const id = slugify(label) || `pregunta_${Date.now()}`;
  return { id, type: 'text', label, required: false };
}

function newSection(label = 'Nueva sección'): FormSection {
  return { id: slugify(label) || `seccion_${Date.now()}`, label, fields: [newField()] };
}

function emptySchema(): AdoptionFormSchema {
  return {
    title: 'Formulario de adopción',
    version: 1,
    language: 'es',
    description: 'Por favor completa este formulario con información veraz.',
    sections: [newSection('Preguntas adicionales')],
  };
}

function cloneSchema(schema?: AdoptionFormSchema): AdoptionFormSchema {
  const base = schema ? JSON.parse(JSON.stringify(schema)) : emptySchema();
  base.sections = Array.isArray(base.sections) && base.sections.length ? base.sections : [newSection()];
  base.sections = base.sections.map((section: any) => ({
    id: section.id || slugify(section.label || section.title || 'seccion'),
    label: section.label || section.title || 'Sección',
    fields: Array.isArray(section.fields) ? section.fields.map(normalizeField) : [],
  }));
  return base;
}

function normalizeField(field: any): FormField {
  const next: any = {
    id: field.id || slugify(field.label || 'pregunta'),
    type: field.type || 'text',
    label: field.label || 'Pregunta',
    required: field.required === true,
  };
  if (field.placeholder) next.placeholder = field.placeholder;
  if (Array.isArray(field.options)) next.options = field.options;
  if (field.binding) next.binding = field.binding;
  const visibleIf = field.visibleIf || (field.conditional?.operator === '==' ? { field: field.conditional.field, equals: field.conditional.value } : undefined);
  if (visibleIf?.field) next.visibleIf = visibleIf;
  return next;
}

function prepareSchema(schema: AdoptionFormSchema): AdoptionFormSchema {
  const clean = cloneSchema(schema);
  clean.sections = clean.sections.map((section) => ({
    id: section.id || slugify(section.label || 'seccion'),
    label: section.label || 'Sección',
    fields: section.fields.map((field) => {
      const next: any = {
        id: field.id || slugify(field.label || 'pregunta'),
        type: field.type,
        label: field.label,
        required: field.required === true,
      };
      if (field.placeholder) next.placeholder = field.placeholder;
      if ((field.type === 'select' || field.type === 'radio') && field.options?.length) next.options = field.options;
      if (field.type === 'yesno') delete next.options;
      if (field.binding?.table && field.binding?.column) next.binding = field.binding;
      if (field.visibleIf?.field) {
        const raw = field.visibleIf.equals;
        next.visibleIf = { field: field.visibleIf.field, equals: raw === 'true' ? true : raw === 'false' ? false : raw };
      }
      return next;
    }),
  }));
  return clean;
}

function moveItem<T>(items: T[], index: number, dir: -1 | 1) {
  const target = index + dir;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export default function FormTemplatesAdminPage() {
  const { isAdminWithShelter, shelterId } = useAuth();
  const isSuperAdmin = !isAdminWithShelter;
  const [loading, setLoading] = useState(true);
  const [loadingActive, setLoadingActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<AdoptionFormTemplate[]>([]);
  const [shelters, setShelters] = useState<any[]>([]);
  const [selectedShelterId, setSelectedShelterId] = useState<number | null>(shelterId ?? null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [formCode, setFormCode] = useState(FORM_CODE);
  const [name, setName] = useState('Formulario de adopción');
  const [description, setDescription] = useState('');
  const [schema, setSchema] = useState<AdoptionFormSchema>(emptySchema);

  const visibleTemplates = useMemo(
    () => templates.filter((t) => (selectedShelterId === null ? t.shelterId == null : Number(t.shelterId) === Number(selectedShelterId))),
    [templates, selectedShelterId]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [templateRows, shelterRows] = await Promise.all([
        adoptionApi.listAdminTemplates(),
        isSuperAdmin ? sheltersApi.list({ filter: { 'fields[id]': true, 'fields[name]': true, order: 'name ASC' } as any }) : Promise.resolve([]),
      ]);
      setTemplates(templateRows);
      setShelters(Array.isArray(shelterRows) ? shelterRows : []);
      const preferred = selectedTemplateId ? templateRows.find((t) => t.id === selectedTemplateId) : templateRows.find((t) => t.isActive && (shelterId ? Number(t.shelterId) === Number(shelterId) : true));
      if (preferred) loadTemplate(preferred);
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'No se pudieron cargar los formularios.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadTemplate = (template: AdoptionFormTemplate) => {
    setSelectedTemplateId(template.id);
    setSelectedShelterId(template.shelterId ?? null);
    setFormCode(template.code || FORM_CODE);
    setName(template.name || template.schemaJson?.title || 'Formulario de adopción');
    setDescription(template.description || template.schemaJson?.description || '');
    setSchema(cloneSchema(template.schemaJson));
  };

  const mergeTemplatesForShelter = (shelterTarget: number | null, rows: AdoptionFormTemplate[]) => {
    setTemplates((prev) => [
      ...prev.filter((template) => (
        shelterTarget === null
          ? template.shelterId != null
          : Number(template.shelterId) !== Number(shelterTarget)
      )),
      ...rows,
    ]);
  };

  const loadLatestForShelter = async (shelterTarget: number | null) => {
    setSelectedShelterId(shelterTarget);
    setSelectedTemplateId(null);
    setLoadingActive(true);
    try {
      const rows = await adoptionApi.listAdminTemplates({
        shelterId: shelterTarget,
      });
      mergeTemplatesForShelter(shelterTarget, rows);
      const latest = rows.find((template) => template.isActive) || rows[0];
      if (latest) {
        loadTemplate(latest);
      } else {
        setName('Formulario de adopción');
        setFormCode(FORM_CODE);
        setDescription('');
        setSchema(emptySchema());
      }
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'No se pudo cargar el formulario activo de la entidad.' });
    } finally {
      setLoadingActive(false);
    }
  };

  const updateSchema = (updater: (prev: AdoptionFormSchema) => AdoptionFormSchema) => {
    setSchema((prev) => updater(cloneSchema(prev)));
  };

  const save = async () => {
    if (isAdminWithShelter && !shelterId) {
      toast({ variant: 'destructive', title: 'Error', description: 'No se encontró la entidad asociada a tu usuario.' });
      return;
    }
    if (!name.trim()) {
      toast({ variant: 'destructive', title: 'Validación', description: 'El nombre del formulario es obligatorio.' });
      return;
    }
    const hasInvalidField = schema.sections.some((section) => !section.label?.trim() || section.fields.some((field) => !field.id?.trim() || !field.label?.trim()));
    if (hasInvalidField) {
      toast({ variant: 'destructive', title: 'Validación', description: 'Todas las secciones y preguntas necesitan título. Cada pregunta necesita ID.' });
      return;
    }

    setSaving(true);
    try {
      const schemaJson = prepareSchema({
        ...schema,
        title: name,
        description,
        language: schema.language || 'es',
        version: Number(schema.version || 1) + 1,
      });
      const created = await adoptionApi.createAdminTemplateVersion({
        shelterId: isAdminWithShelter ? shelterId : selectedShelterId,
        code: formCode || FORM_CODE,
        name,
        description,
        schemaJson,
      });
      toast({ title: `Formulario guardado como versión ${created.version}` });
      await load();
      loadTemplate(created);
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'No se pudo guardar el formulario.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold">Formularios de adopción</h1>
            <p className="text-muted-foreground">Edita preguntas, opciones y condiciones. Cada guardado crea una nueva versión activa.</p>
          </div>
          <Button onClick={save} disabled={saving || loading || loadingActive}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Guardar nueva versión
          </Button>
        </div>

        {loading ? (
          <Card className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando formularios...
          </Card>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[300px,1fr] gap-6">
            <aside className="space-y-4">
              <Card className="p-4 space-y-4">
                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Label>Entidad</Label>
                    <Select
                      value={selectedShelterId === null ? GLOBAL_VALUE : String(selectedShelterId)}
                      onValueChange={(value) => {
                        void loadLatestForShelter(value === GLOBAL_VALUE ? null : Number(value));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona entidad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={GLOBAL_VALUE}>Global</SelectItem>
                        {shelters.map((s) => (
                          <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Versiones</Label>
                    <div className="flex items-center gap-2">
                      {loadingActive && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                      <Badge variant="secondary">{visibleTemplates.length}</Badge>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {visibleTemplates.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Aún no hay versiones para esta entidad.</p>
                    ) : (
                      visibleTemplates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => loadTemplate(template)}
                          className={`w-full rounded-md border p-3 text-left text-sm transition-colors ${selectedTemplateId === template.id ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">Versión {template.version}</span>
                            {template.isActive && <Badge>Activa</Badge>}
                          </div>
                          <p className="mt-1 truncate text-muted-foreground">{template.name}</p>
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setSelectedTemplateId(null);
                    setFormCode(FORM_CODE);
                    setName('Formulario de adopción');
                    setDescription('');
                    setSchema(emptySchema());
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo desde cero
                </Button>
              </Card>
            </aside>

            <main className="space-y-6">
              <Card className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Idioma</Label>
                    <Input value={schema.language || 'es'} onChange={(e) => updateSchema((prev) => ({ ...prev, language: e.target.value || 'es' }))} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="min-h-20" />
                </div>
              </Card>

              {schema.sections.map((section, sectionIndex) => (
                <Card key={`${section.id}-${sectionIndex}`} className="p-5 space-y-5">
                  <div className="flex flex-col gap-3 md:flex-row md:items-end">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
                      <div className="space-y-2">
                        <Label>Sección</Label>
                        <Input
                          value={section.label || ''}
                          onChange={(e) => updateSchema((prev) => {
                            prev.sections[sectionIndex].label = e.target.value;
                            if (!prev.sections[sectionIndex].id) prev.sections[sectionIndex].id = slugify(e.target.value);
                            return prev;
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>ID de sección</Label>
                        <Input
                          value={section.id}
                          onChange={(e) => updateSchema((prev) => {
                            prev.sections[sectionIndex].id = slugify(e.target.value) || e.target.value;
                            return prev;
                          })}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="icon" onClick={() => updateSchema((prev) => ({ ...prev, sections: moveItem(prev.sections, sectionIndex, -1) }))} disabled={sectionIndex === 0}>
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" onClick={() => updateSchema((prev) => ({ ...prev, sections: moveItem(prev.sections, sectionIndex, 1) }))} disabled={sectionIndex === schema.sections.length - 1}>
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="icon" onClick={() => updateSchema((prev) => ({ ...prev, sections: prev.sections.filter((_, i) => i !== sectionIndex) }))} disabled={schema.sections.length === 1}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    {section.fields.map((field, fieldIndex) => (
                      <FieldEditor
                        key={`${field.id}-${fieldIndex}`}
                        field={field}
                        allFields={schema.sections.flatMap((s) => s.fields)}
                        onChange={(next) => updateSchema((prev) => {
                          prev.sections[sectionIndex].fields[fieldIndex] = next;
                          return prev;
                        })}
                        onMove={(dir) => updateSchema((prev) => {
                          prev.sections[sectionIndex].fields = moveItem(prev.sections[sectionIndex].fields, fieldIndex, dir);
                          return prev;
                        })}
                        onDuplicate={() => updateSchema((prev) => {
                          const copy = { ...field, id: `${field.id}_copia` };
                          prev.sections[sectionIndex].fields.splice(fieldIndex + 1, 0, copy);
                          return prev;
                        })}
                        onRemove={() => updateSchema((prev) => {
                          prev.sections[sectionIndex].fields = prev.sections[sectionIndex].fields.filter((_, i) => i !== fieldIndex);
                          return prev;
                        })}
                        canMoveUp={fieldIndex > 0}
                        canMoveDown={fieldIndex < section.fields.length - 1}
                      />
                    ))}
                  </div>

                  <Button variant="outline" onClick={() => updateSchema((prev) => {
                    prev.sections[sectionIndex].fields.push(newField());
                    return prev;
                  })}>
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar pregunta
                  </Button>
                </Card>
              ))}

              <Button variant="outline" onClick={() => updateSchema((prev) => ({ ...prev, sections: [...prev.sections, newSection()] }))}>
                <Plus className="h-4 w-4 mr-2" />
                Agregar sección
              </Button>
            </main>
          </div>
        )}
      </div>
    </Layout>
  );
}

function FieldEditor({
  field,
  allFields,
  onChange,
  onMove,
  onDuplicate,
  onRemove,
  canMoveUp,
  canMoveDown,
}: {
  field: FormField;
  allFields: FormField[];
  onChange: (field: FormField) => void;
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const usesOptions = field.type === 'select' || field.type === 'radio';
  const update = (patch: Partial<FormField>) => onChange({ ...field, ...patch });

  return (
    <div className="rounded-md border p-4 space-y-4 bg-background">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,220px,auto] gap-3 items-end">
        <div className="space-y-2">
          <Label>Pregunta</Label>
          <Input value={field.label} onChange={(e) => update({ label: e.target.value, id: field.id || slugify(e.target.value) })} />
        </div>
        <div className="space-y-2">
          <Label>Tipo</Label>
          <Select value={field.type} onValueChange={(value) => {
            const type = value as FieldType;
            update({
              type,
              options: type === 'select' || type === 'radio' ? (field.options?.length ? field.options : [{ label: 'Opción 1', value: 'option_1' }]) : undefined,
            });
          }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" onClick={() => onMove(-1)} disabled={!canMoveUp}><ArrowUp className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => onMove(1)} disabled={!canMoveDown}><ArrowDown className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={onDuplicate}><Copy className="h-4 w-4" /></Button>
          <Button variant="destructive" size="icon" onClick={onRemove}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="space-y-2">
          <Label>ID</Label>
          <Input value={field.id} onChange={(e) => update({ id: slugify(e.target.value) || e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Texto auxiliar</Label>
          <Input value={field.placeholder || ''} onChange={(e) => update({ placeholder: e.target.value || undefined })} />
        </div>
        <div className="flex items-center gap-2 h-10">
          <Switch checked={field.required === true} onCheckedChange={(checked) => update({ required: checked })} />
          <Label>Obligatoria</Label>
        </div>
      </div>

      {usesOptions && (
        <div className="space-y-2">
          <Label>Opciones</Label>
          <div className="space-y-2">
            {(field.options || []).map((option, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-2">
                <Input
                  value={option.label}
                  placeholder="Etiqueta"
                  onChange={(e) => {
                    const options = [...(field.options || [])];
                    options[index] = { ...options[index], label: e.target.value };
                    update({ options });
                  }}
                />
                <Input
                  value={String(option.value)}
                  placeholder="Valor"
                  onChange={(e) => {
                    const options = [...(field.options || [])];
                    options[index] = { ...options[index], value: e.target.value };
                    update({ options });
                  }}
                />
                <Button variant="outline" size="icon" onClick={() => update({ options: (field.options || []).filter((_, i) => i !== index) })}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => update({ options: [...(field.options || []), { label: 'Nueva opción', value: `option_${(field.options || []).length + 1}` }] })}>
            <Plus className="h-4 w-4 mr-2" />
            Agregar opción
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label>Vincular a perfil</Label>
          <Select
            value={field.binding?.column || GLOBAL_VALUE}
            onValueChange={(value) => update({ binding: value === GLOBAL_VALUE ? undefined : { table: 'adopter_profile', column: value } })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={GLOBAL_VALUE}>Sin vínculo</SelectItem>
              {PROFILE_COLUMNS.map((column) => <SelectItem key={column} value={column}>{column}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Mostrar si pregunta</Label>
          <Select
            value={field.visibleIf?.field || GLOBAL_VALUE}
            onValueChange={(value) => update({ visibleIf: value === GLOBAL_VALUE ? undefined : { field: value, equals: field.visibleIf?.equals ?? 'true' } })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={GLOBAL_VALUE}>Siempre visible</SelectItem>
              {allFields.filter((candidate) => candidate.id !== field.id).map((candidate) => (
                <SelectItem key={candidate.id} value={candidate.id}>{candidate.label || candidate.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Valor esperado</Label>
          <Input
            value={field.visibleIf?.equals === undefined ? '' : String(field.visibleIf.equals)}
            disabled={!field.visibleIf?.field}
            onChange={(e) => update({ visibleIf: field.visibleIf ? { ...field.visibleIf, equals: e.target.value } : undefined })}
          />
        </div>
      </div>
    </div>
  );
}
